"use client";

import { CONTACT_BUDGETS, CONTACT_CATEGORIES } from "@/constants/contact.constants";
import { useContactForm } from "@/hooks/useContactForm";

function baseInput(hasError: unknown): string {
  return `w-full border-0 border-b border-b-[var(--accent)] bg-transparent py-3 text-base text-[var(--text)] outline-none placeholder:text-[var(--subtle)] transition ${
    hasError ? "border-red-500" : "border-[var(--stroke)] focus:border-[var(--accent)]"
  }`;
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm text-[var(--muted)]">{label}</label>
      {children}
      {error ? <p className="mt-1 text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

export default function ContactForm() {
  const {
    form,
    errors,
    touched,
    submitting,
    submitted,
    serverError,
    setField,
    markTouched,
    onSubmit,
  } = useContactForm();

  if (submitted) {
    return (
      <div className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/8 p-10 text-center">
        <p className="text-3xl font-bold text-[var(--text)]">Thanks, {form.name.split(" ")[0]}.</p>
        <p className="mt-3 text-[var(--muted)]">
          Your message is in our queue. A senior engineer will reply within
          one business day at <strong>{form.email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      <Field label="Name*" error={touched.name ? errors.name : undefined}>
        <input
          className={baseInput(touched.name && errors.name)}
          value={form.name}
          onChange={(e) => setField("name", e.target.value)}
          onBlur={() => markTouched("name")}
          autoComplete="name"
        />
      </Field>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label="Email*" error={touched.email ? errors.email : undefined}>
          <input
            type="email"
            className={baseInput(touched.email && errors.email)}
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            onBlur={() => markTouched("email")}
            autoComplete="email"
          />
        </Field>
        <Field label="Phone*" error={touched.phone ? errors.phone : undefined}>
          <input
            type="tel"
            className={baseInput(touched.phone && errors.phone)}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            onBlur={() => markTouched("phone")}
            autoComplete="tel"
          />
        </Field>
      </div>

      <Field label="Select Project Category*" error={touched.category ? errors.category : undefined}>
        <select
          className={`themed-select ${baseInput(touched.category && errors.category)}`}
          value={form.category}
          onChange={(e) => setField("category", e.target.value)}
          onBlur={() => markTouched("category")}
        >
          <option value=""></option>
          {CONTACT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <Field label="Select Budget*" error={touched.budget ? errors.budget : undefined}>
        <select
          className={`themed-select ${baseInput(touched.budget && errors.budget)}`}
          value={form.budget}
          onChange={(e) => setField("budget", e.target.value)}
          onBlur={() => markTouched("budget")}
        >
          <option value=""></option>
          {CONTACT_BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </Field>

      <Field label="Please Describe Your Project*" error={touched.message ? errors.message : undefined}>
        <textarea
          rows={4}
          className={`${baseInput(touched.message && errors.message)} resize-y`}
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          onBlur={() => markTouched("message")}
        />
      </Field>

      <label className="flex items-start gap-3 text-sm text-[var(--muted)]">
        <input
          type="checkbox"
          checked={form.marketing_opt_in}
          onChange={(e) => setField("marketing_opt_in", e.target.checked)}
          className="mt-1 h-5 w-5 rounded border border-[var(--stroke)] bg-transparent"
        />
        <span>I agree to receive marketing communication from Cybitix by email.</span>
      </label>

      {serverError ? <p className="text-sm text-red-400">{serverError}</p> : null}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[var(--accent)] to-[var(--accent-2)] px-8 py-3 text-sm font-semibold text-[#07111f] shadow-[0_0_24px_-6px_var(--accent)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Get an Estimate"}
      </button>
    </form>
  );
}
